import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import { Button, TextInput } from '@/components/ui/daisy';

type LessonInlineFormFields = {
  name: string;
  description: string;
};

type CreateLessonData = {
  name: string;
  description?: string;
  order: number;
};

type LessonInlineFormProps = {
  onSubmit: (data: CreateLessonData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  order: number;
};

export default function LessonInlineForm({
  order,
  onSubmit,
  onCancel,
  isLoading,
}: LessonInlineFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonForm.form',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LessonInlineFormFields>({
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const handleFormSubmit = (data: LessonInlineFormFields) => {
    onSubmit({
      name: data.name,
      description: data.description || undefined,
      order,
    });
  };

  return (
    <FieldsetContainer>
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <TextInput.Fieldset
          label={t('lessonName')}
          labelAlt={errors?.name?.message}
        >
          <TextInput
            type="text"
            placeholder={t('lessonNamePlaceholder')}
            className="w-full"
            color={!errors?.name?.message ? 'default' : 'error'}
            {...register('name', { required: t('lessonNameRequired') })}
          />
        </TextInput.Fieldset>
        <TextInput.Fieldset label={t('lessonDescription')}>
          <TextInput
            type="text"
            placeholder={t('lessonDescriptionPlaceholder')}
            className="w-full"
            {...register('description')}
          />
        </TextInput.Fieldset>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="grow bg-base-300"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            color="secondary"
            className="grow"
            disabled={isLoading}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </FieldsetContainer>
  );
}
